import { useApolloClient } from '@apollo/react-hooks'
import {
  createStyles,
  withStyles,
  Button,
  ConfirmationDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  withClientOptions,
  withCloudinary,
  withDialogStackContext,
  withUser
} from '@paintscout/ui'
import type {
  MuiTheme as Theme,
  WithStyles,
  DialogProps,
  WithClientOptions,
  WithCloudinary,
  WithDialogStackContext,
  WithUser
} from '@paintscout/ui'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import { TRIAL_QUOTE_FILES_LIMIT } from '@paintscout/util'
import { createFile, getFeature, getFiles, getObjectLabels } from '@paintscout/util/builder'
// import { uploadFile } from '@paintscout/util/cloudinary'
import { compressFile, uploadFile } from '@paintscout/util/s3'
import type { ApolloClient } from 'apollo-client'
import type { WithSnackbarProps } from 'notistack'
import { withSnackbar } from 'notistack'
import type { QuoteFile } from 'paintscout'
import React, { useEffect, useState } from 'react'
import { useCompanyQuery, useContactQuery, useDealQuery } from '@paintscout/api'
import type { Company, Contact, Deal } from '@paintscout/api'
import type { WithQuoteContext } from '../../context/withQuoteContext'
import { withQuoteContext } from '../../context/withQuoteContext'
import type { DropzoneError, EnhancedQuoteFile } from './FileView'
import FileView from './FileView'
import ItemFilesStepper from './ItemFilesStepper'
import getErrorInfo from './getErrorInfo'

const FETCH_POLICY_CACHE_AND_NETWORK = 'cache-and-network' as const

export interface EditItemFilesProps {
  /**
   * QuoteFiles to include in dialog
   */
  files?: QuoteFile[]

  /**
   * Automatically starts uploading these files when dialog mounts
   */
  uploadFiles?: File[]

  /**
   * QuoteFiles index to open at
   */
  index?: number

  /**
   * Doc to relate the files to when uploaded
   */
  fileDocId?: string

  /**
   * Only show title input
   */
  titleOnly?: boolean

  /**
   * Loads files from fileDocId item
   */
  loadFiles?: boolean

  /**
   * Type of fileDocId item
   */
  fileDocType?: string // 'contact' | 'company' | 'deal'

  /**
   *  If true, disables any editing functionality
   */
  viewOnly?: boolean

  onConfirm?: (files: QuoteFile[], deletedFiles?: QuoteFile[], fileDoc?: Contact | Company | Deal) => void
  onDelete?: (key: string, files?: EnhancedQuoteFile[]) => void
  onCancel?: () => void
}

type EditItemFilesPropsWithInjections = EditItemFilesProps &
  WithStyles<typeof styles> &
  WithSnackbarProps &
  WithDialogStackContext &
  WithUser &
  WithQuoteContext &
  WithClientOptions &
  WithCloudinary & {
    client: ApolloClient<any>
  }

export interface EditItemFilesState {
  index: number
  files: EnhancedQuoteFile[]
  isDirty: boolean

  /**
   * The total number of quote files excluding this item (so we can calculate how many files a trial user can add)
   */
  numQuoteFiles?: number
}

export const styles = (_theme: Theme) => {
  return createStyles({
    root: {},
    fullHeight: {
      height: '100%'
    },
    content: {
      padding: 16,
      height: '100%'
    },
    stepper: {
      background: 'none'
    },
    paginationActions: {
      padding: 0,
      height: 'initial'
    },
    paginationActionsRightButton: {
      display: 'block'
    }
  })
}

class EditItemFiles extends React.Component<EditItemFilesPropsWithInjections, EditItemFilesState> {
  static defaultProps: Partial<EditItemFilesPropsWithInjections> = {
    files: [],
    index: 0
  }

  constructor(props: EditItemFilesPropsWithInjections) {
    super(props)

    const { quote } = props.quoteContext

    const { index, files = [] } = props

    this.state = {
      index: index || 0,
      files,
      numQuoteFiles: quote ? getFiles({ quote, attached: true }).length - files.length : 0,
      isDirty: false
    }
  }

  public componentDidMount() {
    if (this.props.uploadFiles) {
      this.uploadFiles({ files: this.props.uploadFiles })
    }
  }

  public render() {
    const {
      classes,
      onConfirm: _onConfirm,
      onCancel,
      user,
      quoteContext: _quoteContext,
      clientOptions,
      titleOnly,
      viewOnly
    } = this.props
    const { index, files, numQuoteFiles, isDirty } = this.state
    const currentFile = files[index]
    const uploading = files.some((file) => file.uploading)

    const canAddFiles = !user?.isTrial || numQuoteFiles + files.length < TRIAL_QUOTE_FILES_LIMIT

    const objectLabels = getObjectLabels({ options: clientOptions.options })

    return (
      <>
        <DialogTitle>{files[index] ? 'Edit Media' : 'Add Media'}</DialogTitle>
        <DialogContent classes={{ root: classes.content }}>
          <FileView
            isNew={!currentFile}
            file={currentFile}
            disabled={!canAddFiles}
            viewOnly={viewOnly}
            titleOnly={titleOnly}
            disabledMsg={`You must upgrade your plan to add more files to this ${objectLabels.quote.value}`}
            onChange={this.handleFileChange}
            onDelete={this.handleFileDelete}
            onUpload={this.handleNewFiles}
            onReplace={(file) => this.handleReplaceFile(file, this.state.index)}
            onDropzoneError={this.handleDropzoneError}
            onRetry={this.handleRetry}
          />
        </DialogContent>
        <DialogActions
          classes={{
            root: classes.paginationActions,
            rightButton: classes.paginationActionsRightButton
          }}
        >
          <ItemFilesStepper
            activeStep={index}
            position="static"
            files={files}
            addIcon
            nextButton={
              <Button
                data-testid="files-next-button"
                variant="text"
                color="primary"
                disabled={index === files.length}
                onClick={() => this.setState({ index: index + 1 })}
              >
                Next
                <KeyboardArrowRightIcon />
              </Button>
            }
            backButton={
              <Button
                data-testid="files-back-button"
                variant="text"
                color="primary"
                disabled={index === 0}
                onClick={() => this.setState({ index: index - 1 })}
              >
                <KeyboardArrowLeftIcon />
                Back
              </Button>
            }
          />
        </DialogActions>
        <DialogActions
          leftButton={
            <Button
              onClick={() => {
                if (isDirty) {
                  this.props.dialogStackContext.openDialog(ConfirmationDialog, {
                    message: 'Newly added images will be lost',
                    onConfirm: () => {
                      this.props.dialogStackContext.dismissDialog()
                      onCancel()
                    },
                    onCancel: () => {
                      this.props.dialogStackContext.dismissDialog()
                    }
                  })
                } else {
                  onCancel()
                }
              }}
              variant="textNoPadding"
            >
              Cancel
            </Button>
          }
        >
          <Button
            type="submit"
            variant={'contained'}
            onClick={this.handleDialogConfirm}
            disabled={files.filter((file) => file.uploading).length > 0}
            loading={uploading}
          >
            Done
          </Button>
        </DialogActions>
      </>
    )
  }

  public handleFileChange = (file: EnhancedQuoteFile) => {
    const { files } = this.state
    const fileIndex = files.findIndex(({ key }) => key === file.key)

    if (fileIndex !== -1) {
      const [cleanFile] = cleanEnhancedFiles([file])

      this.setState({ files: [...files.slice(0, fileIndex), cleanFile, ...files.slice(fileIndex + 1)] })
    }
  }

  public handleFileDelete = (key: string) => {
    const { onDelete } = this.props
    const { files } = this.state
    const fileIndex = files.findIndex((file) => file.key === key)
    const newFiles = fileIndex !== -1 ? [...files.slice(0, fileIndex), ...files.slice(fileIndex + 1)] : files
    // Pass post-deletion files so callers don't need to re-filter
    onDelete(key, newFiles)
    this.setState({ files: newFiles })
  }

  public handleDialogConfirm = (_event: any) => {
    const { onConfirm } = this.props

    const filesWithErrors = this.state.files.filter((file) => !!file.error)
    const filesWithLongTitles = this.state.files.filter((file) => file.title && file.title.length > 100)

    const files = this.state.files
    const deletedFiles = this.props.files.filter(
      (file) => !this.state.files.find((stateFile) => stateFile.key === file.key)
    )
    if (filesWithLongTitles.length > 0) {
      const multiLongTitles = filesWithLongTitles.length > 1
      this.props.enqueueSnackbar(
        `${multiLongTitles ? `Some Media items have titles` : `A Media item has a title`} longer than 100 characters. Please shorten ${multiLongTitles ? 'them' : 'it'} before saving.`,
        { variant: 'warning' }
      )
      return
    }
    if (filesWithErrors.length > 0) {
      this.props.dialogStackContext.openDialog(ConfirmationDialog, {
        message: 'Some Media items have errors. Are you sure you want to save?',
        onConfirm: () => {
          if (onConfirm) {
            const cleanFiles = cleanEnhancedFiles(files)
            onConfirm(cleanFiles, deletedFiles)
          }
          this.props.dialogStackContext.dismissDialog()
        },
        onCancel: this.props.dialogStackContext.dismissDialog
      })
    } else {
      if (onConfirm) {
        onConfirm(files, deletedFiles)
      }
    }
  }

  public handleNewFiles = async (files: File[]) => {
    if (this.props.user?.isTrial) {
      const filesLeft = TRIAL_QUOTE_FILES_LIMIT - (this.state.numQuoteFiles + this.state.files.length)

      this.uploadFiles({
        files: files.slice(0, filesLeft)
      })
    } else {
      this.uploadFiles({ files })
    }
  }

  public handleReplaceFile = async (file: File, index: number) => {
    const quoteFile = this.state.files[index]
    this.uploadFiles({ files: [file], quoteFiles: [quoteFile] })
  }

  public uploadFile = async (file: File) => {
    let fileToUpload = file
    const { clientOptions } = this.props
    const { options } = clientOptions
    const s3UploadFeatureEnabled = getFeature({ options, path: 's3ImageUpload.enabled' })

    // compress if image
    if (file.type.startsWith('image') && !file.type.includes('svg')) {
      fileToUpload = await compressFile({
        file
      })

      // console.log(`Resize image: ${file.size}->${fileToUpload.size}`)
    }

    const data = await uploadFile({
      file: fileToUpload,
      apolloClient: this.props.client,
      uploadOptions: {
        companyId: this.props.user?.user?.app_metadata?.companyId,
        docId: this?.props?.fileDocId ?? this.props.quoteContext?.quote?._id,
        s3UploadFeatureEnabled
      }
    })

    return data
  }

  /**
   * Retries all retryable failed files
   */
  public handleRetry = async () => {
    const failedQuoteFiles = this.state.files.filter((file) => !!file.failedFile && getErrorInfo(file.error).retryable)

    await this.uploadFiles({
      quoteFiles: failedQuoteFiles,
      files: failedQuoteFiles.map((quoteFile) => quoteFile.failedFile)
    })
  }

  /**
   * files are the raw files from the upload
   *
   * if quoteFiles is provided, it'll update the quoteFiles with the uploaded file (in order of array, 1 file to quoteFile)
   *
   * if it's not provided, it'll create a new quoteFile with the uploaded file and add it to the state
   */
  public uploadFiles = async (args: { files: File[]; quoteFiles?: QuoteFile[] }) => {
    // if the upload fails we'll want to set the src back to what it was before
    const originalSourceValues = args.quoteFiles
      ? args.quoteFiles.reduce(
          (srcs, quoteFile) => ({
            ...srcs,
            [quoteFile.key]: { src: quoteFile.src, cloudinaryPublicId: quoteFile.cloudinaryPublicId }
          }),
          {}
        )
      : {}

    // create new quote files or update existing ones
    const editingQuoteFiles = args.files.map((file, i) => {
      const quoteFile =
        args.quoteFiles && args.quoteFiles[i]
          ? args.quoteFiles[i]
          : createFile({ type: file.type, title: this.props?.titleOnly ? file.name : '' })
      return {
        ...quoteFile,
        title: file?.name ?? quoteFile.title,
        type: file.type,
        uploading: true,
        src: null,
        cloudinaryPublicId: null,
        error: null,
        failedFile: null
      }
    })

    // determine which ones are new
    const newQuoteFiles = editingQuoteFiles.filter(({ key }) => !this.state.files.find((f) => f.key === key))

    // update the files in state
    this.setState({
      files: [
        ...this.state.files.map((quoteFile) => {
          const edited = editingQuoteFiles.find((af) => af.key === quoteFile.key)

          return edited ? edited : quoteFile
        }),
        ...newQuoteFiles
      ],
      isDirty: true
    })

    const funcs = editingQuoteFiles.map((quoteFile, index) => async () => {
      try {
        const uploadRes = await this.uploadFile(args.files[index])

        // update src after successful upload
        this.setState((prevState) => ({
          files: prevState.files.map((prevQuoteFile) => {
            if (prevQuoteFile.key === quoteFile.key) {
              return {
                ...prevQuoteFile,
                title: uploadRes.title || prevQuoteFile.title,
                height: uploadRes.height,
                width: uploadRes.width,
                uploading: false,
                src: uploadRes.src,
                cloudinaryPublicId: uploadRes?.cloudinaryPublicId ?? '',
                s3PublicKey: uploadRes?.s3PublicKey ?? ''
              }
            }

            return prevQuoteFile
          })
        }))
        return {
          success: true,
          key: quoteFile.key
        }
      } catch (error) {
        this.setState((prevState) => ({
          files: prevState.files.map((prevQuoteFile) => {
            if (prevQuoteFile.key === quoteFile.key) {
              const originalSource = originalSourceValues[prevQuoteFile.key] || {
                src: null,
                cloudinaryPublicId: null,
                s3PublicKey: null
              }

              return {
                ...prevQuoteFile,
                ...originalSource,
                uploading: false,
                failedFile: args.files[index]
              }
            }

            return prevQuoteFile
          })
        }))

        console.log({ error })
        return {
          error: error,
          key: quoteFile.key
        }
      }
    })

    const results = await limitConcurrency(funcs, 10)
    const errors = results.filter((res) => !!res?.error)

    if (errors.length > 0) {
      if (errors.length === 1) {
        this.props.enqueueSnackbar('Unable to upload file', { variant: 'error' })
      } else {
        this.props.enqueueSnackbar(`Unable to upload ${errors.length} files`, { variant: 'error' })
      }
    }

    // update files uploading status and include any errors
    this.setState((prevState) => {
      const errorsByKey = errors.reduce(
        (total, res) => {
          if (res.error) {
            // api error response
            if (res.error.response && res.error.response.data) {
              return {
                ...total,
                [res.key]: res.error.response.data.name
              }
            }
            // axios error
            else if (res.error.message) {
              return {
                ...total,
                [res.key]: res.error.message
              }
            }
          }

          return total
        },
        {} as Record<string, string>
      )

      return {
        files: prevState.files.map((file) => {
          const error = errorsByKey[file.key] || null

          return {
            ...file,
            uploading: false,
            error: error ? error : file.error
          }
        })
      }
    })
  }

  public handleDropzoneError = (errors: DropzoneError[]) => {
    const errorsWithoutKey = errors.filter((file) => !file.key)
    const errorsWithKey = errors.filter((file) => !!file.key)

    this.setState({
      files: [
        ...this.state.files.map((file) => {
          const errorFile = errorsWithKey.find((f) => f.key === file.key)

          if (errorFile) {
            return {
              ...file,
              error: errorFile.error
            }
          }

          return file
        }),
        ...errorsWithoutKey.map((error) => ({
          ...createFile({ type: error.file.type }),
          error: error.error
        }))
      ]
    })
  }
}

const EditItemFilesWithHOCs = withStyles(styles)(
  withSnackbar(withDialogStackContext(withUser(withQuoteContext(withClientOptions(withCloudinary(EditItemFiles))))))
)

function EditItemFilesDialog(props: DialogProps & EditItemFilesProps) {
  const {
    files,
    index,
    uploadFiles,
    loadFiles,
    fileDocType,
    onConfirm,
    titleOnly,
    fileDocId,
    onDelete,
    onCancel,
    viewOnly,
    ...baseDialogProps
  } = props

  // easiest way to get apolloClient into our class component
  // but we probably should refactor EditItemFiles into a functional component
  // if we do any more work on it
  const apolloClient = useApolloClient()

  const [fileDoc, setFileDoc] = useState(null)
  const [docFiles, setDocFiles] = useState<QuoteFile[]>([])
  const {
    data: contactData,
    loading: _isLoadingContact,
    error: _contactError
  } = useContactQuery({
    variables: { id: fileDocId },
    skip: !loadFiles || fileDocType !== 'contact',
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK
  })

  const {
    data: companyData,
    loading: _isLoadingCompany,
    error: _companyError
  } = useCompanyQuery({
    variables: { id: fileDocId }, // uses docId as it can only be found on itself
    skip: !loadFiles || fileDocType !== 'company',
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK
  })

  const {
    data: dealData,
    loading: _isLoadingDeal,
    error: _dealError
  } = useDealQuery({
    variables: { id: fileDocId },
    skip: !loadFiles || fileDocType !== 'deal',
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK
  })

  useEffect(() => {
    if (contactData) {
      setFileDoc(contactData.contact)
    } else if (companyData) {
      setFileDoc(companyData.company)
    } else if (dealData) {
      setFileDoc(dealData.deal)
    }
  }, [contactData, companyData, dealData])

  useEffect(() => {
    if (fileDoc) {
      const docFiles = Object.values(fileDoc?.files ?? {}).filter((file: QuoteFile) =>
        files.some((f) => f.key === file.key)
      ) as QuoteFile[]
      setDocFiles(docFiles)
    }
  }, [fileDoc])

  return (
    <Dialog {...baseDialogProps} maxWidth="md" fullWidth={true}>
      {loadFiles && !docFiles.length ? (
        <></>
      ) : (
        <EditItemFilesWithHOCs
          client={apolloClient}
          files={loadFiles ? docFiles : files}
          uploadFiles={uploadFiles}
          viewOnly={viewOnly}
          index={index}
          onConfirm={(files, deletedFiles) => onConfirm(files, deletedFiles, fileDoc)}
          onDelete={onDelete}
          onCancel={onCancel}
          titleOnly={titleOnly}
          fileDocId={fileDocId}
        />
      )}
    </Dialog>
  )
}

export default EditItemFilesDialog

// removes extra values from EnhancedQuoteFile
function cleanEnhancedFiles(files: EnhancedQuoteFile[]): QuoteFile[] {
  return files.map(({ uploading: _uploading, error: _error, failedFile: _failedFile, ...cleanFile }) => {
    return cleanFile
  })
}

export async function limitConcurrency(funcs, limit) {
  const results = []
  const queue = [...funcs]

  const worker = async () => {
    while (queue.length > 0) {
      const func = queue.shift()
      if (func) {
        const result = await func().catch((error) => ({ error }))
        results.push(result)
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, funcs.length) }, worker))
  return results
}
